/* eslint-disable react-hooks/set-state-in-effect --
 *
 * This hook's job is to bridge the async tRPC `auth.me` query into the
 * synchronous React state our consumers expect. Every effect below
 * deliberately converts React Query's `data` / `error` / `isFetching`
 * into local `user` / `isLoading` / rate-limit state, which is exactly
 * what set-state-in-effect flags. Replacing with `useSyncExternalStore`
 * would need access to React Query internals we don't have; this is the
 * standard pattern for this integration point.
 */
import { useEffect, useState, useCallback } from "react";
import { trpc } from "../lib/trpc";
import { toast } from "sonner";
import type { MeResponse } from "../../../api/src/trpc/routers/auth";

export interface User {
  id: string;
  email: string;
  name: string | null;
  /** Session org id; null for guests (project access through collaborator rows only). */
  tenantId: string | null;
  /** Role in the session org; null for guests. */
  role: string | null;
  /** Staff capability: a SAML session in the internal org (SP1 §4.2). */
  isInternal?: boolean;
  amr?: MeResponse["amr"];
  org?: MeResponse["org"];
  /** Orgs the user can switch to (names only). */
  orgs?: MeResponse["orgs"];
}

/** The local user from `auth.me` (`{ id, name, email, role, amr, org, orgs }`). */
export function userFromMe(me: MeResponse): User {
  return {
    id: me.id,
    email: me.email,
    name: me.name,
    tenantId: me.org?.id ?? null,
    role: me.role,
    isInternal: me.amr === "saml" && !!me.org?.isInternal,
    amr: me.amr,
    org: me.org,
    orgs: me.orgs,
  };
}

/**
 * Authentication hook
 * Manages user state and provides auth utilities
 *
 * SECURITY: Uses httpOnly cookies for authentication
 * - Tokens are never stored in localStorage
 * - Tokens are never accessible to JavaScript
 * - Protection against XSS token theft
 */
export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);
  const [suspendAuthChecks, setSuspendAuthChecks] = useState(false);

  // Clear rate limit flag after the cooldown ends
  useEffect(() => {
    if (!rateLimitedUntil) {
      return;
    }
    const now = Date.now();
    const delay = Math.max(0, rateLimitedUntil - now);
    const timeout = setTimeout(() => setRateLimitedUntil(null), delay);
    return () => clearTimeout(timeout);
  }, [rateLimitedUntil]);

  // Query user data - authentication happens via httpOnly cookies
  const { data, isLoading: isFetching, error, refetch } = trpc.auth.me.useQuery(
    undefined,
    {
      retry: false, // Don't retry on auth failure
      refetchOnWindowFocus: false, // Don't refetch on every focus
      refetchInterval: false,
      gcTime: 5 * 60 * 1000, // keep cached for 5 minutes (React Query v5)
      staleTime: 5 * 60 * 1000, // avoid unnecessary re-fetching
      // The useEffect below clears rateLimitedUntil when the cooldown ends,
      // so we can treat it as a pure flag here without calling Date.now().
      enabled: !suspendAuthChecks && !rateLimitedUntil,
    }
  );

  // Logout mutation - clears httpOnly cookies on server
  const logoutMutation = trpc.auth.logout.useMutation();
  const trpcUtils = trpc.useUtils();

  useEffect(() => {
    if (data) {
      setUser(userFromMe(data));
      setSuspendAuthChecks(false); // Resume checks when we have a valid session
      setIsLoading(false);
    } else if (!isFetching) {
      // Query finished but no data - stop loading
      setIsLoading(false);
    } else {
      setIsLoading(isFetching);
    }
  }, [data, isFetching]);

  useEffect(() => {
    if (error) {
      // Halt further auth.me calls on 401 to avoid hammering
      const httpStatus = (error.data as { httpStatus?: number } | null | undefined)?.httpStatus;
      const message = error.message?.toLowerCase() || "";
      if (httpStatus === 401 || message.includes("unauthorized")) {
        setSuspendAuthChecks(true);
        setUser(null);
        setIsLoading(false);
        return;
      }

      // Respect upstream rate limiting to avoid hammering auth
      const is429 =
        (error.data as { httpStatus?: number } | null | undefined)?.httpStatus === 429 ||
        error.message?.includes("429") ||
        error.message?.toLowerCase().includes("too many requests");

      if (is429) {
        const cooldownMs = 30_000;
        setRateLimitedUntil(Date.now() + cooldownMs);
        setUser(null);
        setIsLoading(false);
        toast.warning("Please wait a few seconds before retrying login.");
        return;
      }

      // Token invalid or expired
      setUser(null);
      setIsLoading(false);
    }
  }, [error]);

  // Ensure loading stops when auth checks are suspended or rate limited.
  // rateLimitedUntil is cleared by the earlier effect when the cooldown ends,
  // so its presence alone means "still rate limited".
  useEffect(() => {
    if (suspendAuthChecks || rateLimitedUntil) {
      setIsLoading(false);
    }
  }, [suspendAuthChecks, rateLimitedUntil]);

  /**
   * Set user after successful authentication
   * Tokens are already in httpOnly cookies - no need to pass them
   *
   * CRITICAL: Memoized to prevent infinite re-renders in components that
   * depend on this function (e.g., SAMLCallbackPage useEffect)
   */
  const login = useCallback((userData: User) => {
    setUser(userData);
    setSuspendAuthChecks(false); // Allow future auth checks now that we're logged in
  }, []);

  /**
   * Logout user.
   *
   * Hits the tRPC logout mutation (which deletes server sessions + clears
   * httpOnly cookies), then fully resets local React Query state so the
   * auth.me query can't re-hydrate `user` from its cache after the
   * cookies are gone. `suspendAuthChecks` is flipped so the subsequent
   * refetch doesn't immediately re-fire against the cleared cookies and
   * surface a 401 toast.
   *
   * Finally a hard redirect to / ensures the SPA unmounts every page-
   * level hook that was subscribed to the authed user (Socket.IO, usage
   * of Presence etc.) — otherwise stale subscriptions linger until the
   * user manually navigates.
   */
  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (err) {
      console.error("Logout failed on server (clearing locally anyway):", err);
    }
    setUser(null);
    setSuspendAuthChecks(true);
    await trpcUtils.auth.me.reset();
    await trpcUtils.invalidate();
    window.location.href = "/login";
  }, [logoutMutation, trpcUtils]);

  /**
   * Refresh user data from server
   */
  const refreshUser = useCallback(() => {
    setSuspendAuthChecks(false); // Ensure auth.me can run
    refetch();
  }, [refetch]);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
  };
}
